/**
 * Seeded RNG.
 *
 * Generation is a randomised search, so a board is only reproducible if the
 * randomness is. Each attempt draws its own seed and runs on its own generator,
 * which is what lets the seed printed under a finished puzzle mean something: it
 * names that board rather than the run that happened to find it.
 */

export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform integer in [0, n). */
  int(n: number): number;
  /** Fisher–Yates, in place, returning the same array. */
  shuffle<T>(items: T[]): T[];
  pick<T>(items: readonly T[]): T;
}

/** mulberry32: small, fast, and good enough for shuffling a grid. */
export function makeRng(seed: number): Rng {
  let state = seed >>> 0;
  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const int = (n: number): number => Math.floor(next() * n);
  return {
    next,
    int,
    shuffle<T>(items: T[]): T[] {
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = int(i + 1);
        const a = items[i] as T;
        items[i] = items[j] as T;
        items[j] = a;
      }
      return items;
    },
    pick<T>(items: readonly T[]): T {
      return items[int(items.length)] as T;
    },
  };
}

export function randomSeed(): number {
  const buffer = new Uint32Array(1);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(buffer);
    return buffer[0] as number;
  }
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}
